<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1100] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transform transition-all flex flex-col max-h-[92vh] sm:max-h-[88vh]"
    >
      <!-- Modal Header -->
      <div
        class="px-5 sm:px-6 py-4 bg-gradient-to-r from-purple-600 via-indigo-600 to-indigo-800 text-white flex items-center justify-between flex-shrink-0"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0"
          >
            <Layers class="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 class="text-base sm:text-lg font-bold">Configure Class Fee</h3>
            <p class="text-[11px] sm:text-xs text-indigo-100 font-medium">
              Set admission and recurring monthly fee details for an entire class
            </p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Close modal"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Class Selection Bar -->
      <div class="bg-indigo-50/70 border-b border-indigo-100 px-5 sm:px-6 py-3 space-y-2.5 flex-shrink-0">
        <div>
          <label class="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
            Select Class
          </label>
          <select
            v-model="selectedClassId"
            @change="loadClassStructure"
            class="w-full px-3 py-2 text-sm font-semibold rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm"
          >
            <option value="" disabled>-- Select a Class --</option>
            <option
              v-for="cls in classes"
              :key="cls._id"
              :value="cls._id"
            >
              {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
            </option>
          </select>
        </div>

        <!-- Class Info & Student Count Badge -->
        <div
          v-if="selectedClassId"
          class="flex items-center justify-between bg-white px-3.5 py-2 rounded-xl border border-indigo-200/80 shadow-xs"
        >
          <div class="flex items-center space-x-2">
            <Users class="w-4 h-4 text-indigo-600" />
            <span class="text-xs font-semibold text-slate-700">
              Enrolled Students:
            </span>
          </div>
          <span
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 font-mono"
          >
            {{ isFetchingClass ? "..." : `${studentCount} student(s)` }}
          </span>
        </div>
      </div>

      <!-- Loading State while fetching class fee structure -->
      <div v-if="isFetchingClass" class="p-12 text-center flex-1 flex flex-col items-center justify-center">
        <AppLoader
          variant="card"
          size="md"
          message="Loading Class Fee Details..."
          subtext="Fetching fee heads and academic structure"
          color="indigo"
        />
      </div>

      <!-- Form Body with Scrollable Area & Fixed Action Footer -->
      <form
        v-else-if="selectedClassId"
        @submit.prevent="handleSubmit"
        class="flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <!-- Scrollable content area -->
        <div class="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-4">
          <!-- 1. One-Time Admission Fee -->
          <div class="p-3.5 sm:p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2.5">
            <div class="flex items-center justify-between">
              <label class="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <GraduationCap class="w-4 h-4 text-indigo-600" />
                Class One-Time Admission Fee (₹)
              </label>
              <span class="text-[10px] sm:text-[11px] text-slate-500">Charged once per student</span>
            </div>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400 font-bold">₹</span>
              <input
                v-model.number="form.admissionFee"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                required
                class="w-full pl-8 pr-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              />
            </div>
            <p class="text-[11px] text-slate-500">
              Standard admission fee charged to each student belonging to this class.
            </p>
          </div>

          <!-- 2. Recurring Monthly Fee -->
          <div class="p-3.5 sm:p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3.5">
            <div class="flex items-center justify-between">
              <label class="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <Calendar class="w-4 h-4 text-purple-600" />
                Class Recurring Monthly Fee (₹)
              </label>
              <span class="text-[10px] sm:text-[11px] text-slate-500">Billed monthly</span>
            </div>

            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400 font-bold">₹</span>
              <input
                v-model.number="form.monthlyFee"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                required
                class="w-full pl-8 pr-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
              />
            </div>

            <!-- Billing Start Month and Year -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1">
                  Start Month
                </label>
                <select
                  v-model.number="form.startMonth"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option v-for="(m, idx) in months" :key="idx" :value="idx + 1">
                    {{ m }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1">
                  Start Year
                </label>
                <input
                  v-model.number="form.startYear"
                  type="number"
                  min="2020"
                  max="2035"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>

            <!-- Due Day & Policy -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1">
                  Due Day of Month
                </label>
                <input
                  v-model.number="form.dueDay"
                  type="number"
                  min="1"
                  max="31"
                  placeholder="10"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
                <span class="text-[10px] text-slate-400">e.g. 10 = 10th of month</span>
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-600 mb-1">
                  Joining Policy
                </label>
                <select
                  v-model="form.joinPolicy"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="full_month">Full Month Charge</option>
                  <option value="next_month">Bill Next Month</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Academic Session & Remarks -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-600 mb-1">
                Academic Session
              </label>
              <input
                v-model="form.academicSession"
                type="text"
                list="class-session-options"
                placeholder="e.g. 2025-2026"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <datalist id="class-session-options">
                <option v-for="s in sessionOptions" :key="s" :value="s" />
              </datalist>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 mb-1">
                Remarks (Optional)
              </label>
              <input
                v-model="form.remarks"
                type="text"
                placeholder="e.g. Standard rate 2026"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>

          <!-- Apply to All Students Checkbox -->
          <div class="p-3.5 bg-purple-50/70 border border-purple-200/80 rounded-xl space-y-1">
            <label class="flex items-start space-x-2.5 cursor-pointer">
              <input
                v-model="form.applyToAllStudents"
                type="checkbox"
                class="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4 border-slate-300"
              />
              <div>
                <span class="text-xs font-bold text-slate-800 block">
                  Apply to all {{ studentCount }} existing students in this class
                </span>
                <p class="text-[11px] text-slate-600 leading-relaxed mt-0.5">
                  Automatically updates each student's admission fee target and monthly recurring rates. Existing payments are preserved.
                </p>
              </div>
            </label>
          </div>
        </div>

        <!-- Sticky Action Buttons Footer -->
        <div class="p-3.5 sm:p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            @click="$emit('close')"
            :disabled="isSubmitting"
            class="px-4 py-2 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting || !selectedClassId"
            class="px-5 py-2.5 text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl shadow-md shadow-indigo-500/25 transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <AppLoader
              v-if="isSubmitting"
              variant="button"
              class="w-4 h-4 text-white"
            />
            <span>{{ isSubmitting ? "Saving..." : "Save & Apply to Class" }}</span>
          </button>
        </div>
      </form>

      <!-- Empty State if no class selected -->
      <div v-else class="flex-1 p-8 text-center text-slate-500 text-xs font-medium flex items-center justify-center">
        Please select a class from the dropdown above to view and configure its fee structure.
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, watch } from "vue";
  import { X, Layers, GraduationCap, Calendar, Users } from "lucide-vue-next";
  import { getClassFeeStructure } from "@/services/feeService";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  const props = defineProps({
    show: { type: Boolean, default: false },
    initialClassId: { type: String, default: "" },
    classes: { type: Array, default: () => [] },
    availableSessions: { type: Array, default: () => [] },
    isSubmitting: { type: Boolean, default: false },
  });

  const emit = defineEmits(["close", "save"]);
  const { showToast } = useGlobalToast();

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const now = new Date();
  const selectedClassId = ref("");
  const studentCount = ref(0);
  const isFetchingClass = ref(false);

  const currYear = now.getFullYear();
  const defaultSessionOptions = [
    `${currYear + 1}-${currYear + 2}`,
    `${currYear}-${currYear + 1}`,
    `${currYear - 1}-${currYear}`,
    `${currYear - 2}-${currYear - 1}`,
  ];
  const sessionOptions = computed(() => {
    const list = new Set([...(props.availableSessions || []), ...defaultSessionOptions]);
    return Array.from(list).sort((a, b) => b.localeCompare(a));
  });

  const form = reactive({
    admissionFee: 0,
    monthlyFee: 0,
    startMonth: now.getMonth() + 1,
    startYear: now.getFullYear(),
    dueDay: 10,
    joinPolicy: "full_month",
    academicSession: "",
    remarks: "",
    applyToAllStudents: true,
  });

  const loadClassStructure = async () => {
    if (!selectedClassId.value) return;
    try {
      isFetchingClass.value = true;
      const res = await getClassFeeStructure(selectedClassId.value);
      if (res.data?.success) {
        const d = res.data.data;
        studentCount.value = d.studentCount || 0;
        const fs = d.feeStructure || {};
        form.admissionFee = fs.admissionFee || 0;
        form.monthlyFee = fs.monthlyFee || 0;
        form.startMonth = fs.startMonth || now.getMonth() + 1;
        form.startYear = fs.startYear || now.getFullYear();
        form.dueDay = fs.dueDay || 10;
        form.joinPolicy = fs.joinPolicy || "full_month";
        form.academicSession = fs.academicSession || "";
        form.remarks = fs.remarks || "";
        form.applyToAllStudents = true;
      }
    } catch (err) {
      console.error("loadClassStructure error:", err);
      showToast("error", "Failed to fetch class fee details");
    } finally {
      isFetchingClass.value = false;
    }
  };

  watch(
    () => props.show,
    (isOpen) => {
      if (isOpen) {
        selectedClassId.value =
          props.initialClassId || (props.classes.length > 0 ? props.classes[0]._id : "");
        if (selectedClassId.value) {
          loadClassStructure();
        }
      }
    },
    { immediate: true }
  );

  watch(
    () => props.initialClassId,
    (newVal) => {
      if (props.show && newVal) {
        selectedClassId.value = newVal;
        loadClassStructure();
      }
    }
  );

  const handleSubmit = () => {
    if (!selectedClassId.value) {
      showToast("error", "Please select a class");
      return;
    }
    emit("save", {
      classId: selectedClassId.value,
      ...form,
    });
  };
</script>
